import {isLoggedIn} from '@github-ui/client-env'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useOptimisticData} from '@github-ui/optimistic-connection/useOptimisticData'
import {useQuery, useQueryClient, useSuspenseQuery} from '@github-ui/react-query'
import {ReactionViewer} from '@github-ui/reaction-viewer/ReactionViewer'
import type {ReactionViewerGroupsFragment} from '@github-ui/reaction-viewer/ReactionViewerTypes'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {IssueCommentViewerRefetchServerQuery} from '@github-ui/server-persisted-queries'
import type {IssueCommentViewerRefetchServerQueryQuery} from '@github-ui/server-persisted-queries/types/IssueCommentViewerRefetchServerQuery'
import {useInvalidateOnAliveUpdate} from '@github-ui/use-invalidate-on-alive-update'
import {useSyncedState} from '@github-ui/use-synced-state'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {clsx} from 'clsx'
import type React from 'react'
import {Suspense, useCallback, useEffect, useMemo, useRef} from 'react'

import {CLASS_NAMES} from '../../constants/dom-elements'
import {TEST_IDS} from '../../constants/test-ids'
import {VALUES} from '../../constants/values'
import type {OptimisticConnection} from '../../mutations/comment-connection-overlay'
import {getQuotedText} from '../../utils/quotes'
import {type CommentAuthorType, CommentAuthorTypes} from '../ActivityHeader'
import {CommentLoading} from '../CommentLoading'
import {fetchGhostUser, GHOST_USER_QUERY_KEY} from './fetch-ghost-user'
import type {IssueCommentNode, IssueCommentViewerMarkdownViewerNode} from './issue-viewer-page-query-types'
import {IssueCommentHeader} from './IssueCommentHeader'
import styles from './IssueCommentViewer.module.css'
import {IssueMarkdownViewer} from './IssueMarkdownViewer'
import {RefreshVideoWrapper} from './RefreshVideoWrapper'

// ReactionViewerGroupsFragment is derived from IssueViewerPage SPQ types.
export type IssueCommentViewerReactableFragment = ReactionViewerGroupsFragment

type IssueCommentViewerAuthor = NonNullable<IssueCommentNode['author']>
type IssueCommentViewerNamedAuthor = Extract<
  IssueCommentViewerAuthor,
  {readonly __typename: 'User' | 'EnterpriseUserAccount' | 'Mannequin'}
>
type IssueCommentViewerRepository = NonNullable<IssueCommentNode['repository']>
type IssueCommentViewerIssue = NonNullable<IssueCommentNode['issue']>
type IssueCommentViewerViaApp = NonNullable<IssueCommentNode['viaApp']>
type IssueCommentViewerSponsorship = NonNullable<IssueCommentNode['authorToRepoOwnerSponsorship']>
type IssueCommentViewerLastUserContentEdit = NonNullable<IssueCommentNode['lastUserContentEdit']>
type IssueCommentViewerEditor = NonNullable<IssueCommentViewerLastUserContentEdit['editor']>
type IssueCommentViewerPinnedBy = NonNullable<IssueCommentNode['pinnedBy']>

export type IssueCommentViewerCommentRowFragment = Pick<
  IssueCommentNode,
  | 'id'
  | 'databaseId'
  | 'url'
  | 'createdAt'
  | 'authorAssociation'
  | 'viewerCanDelete'
  | 'viewerCanMinimize'
  | 'viewerCanUnminimize'
  | 'viewerCanReport'
  | 'viewerCanReportToMaintainer'
  | 'viewerCanBlockFromOrg'
  | 'viewerCanUnblockFromOrg'
  | 'viewerCanPin'
  | 'viewerCanUnpin'
  | 'minimizedReason'
  | 'showSpammyBadge'
  | 'createdViaEmail'
  | 'viewerDidAuthor'
  | 'viewerCanReadUserContentEdits'
  | 'lastEditedAt'
  | 'body'
  | 'bodyHTML'
  | 'bodyVersion'
  | 'isHidden'
  | 'viewerCanUpdate'
> & {
  readonly author:
    | (Pick<IssueCommentViewerAuthor, 'id' | 'avatarUrl' | 'login' | 'profileUrl' | '__typename'> & {
        readonly name?: IssueCommentViewerNamedAuthor['name']
      })
    | null
  // The component only reads fields common to user and organization owners.
  readonly repository: Pick<
    IssueCommentViewerRepository,
    'id' | 'name' | 'nameWithOwner' | 'isPrivate' | 'viewerCanCreateIssues'
  > & {
    readonly owner: {readonly id: string; readonly login: string; readonly url: string}
  }
  readonly issue: Pick<IssueCommentViewerIssue, 'id' | 'number' | 'locked'>
  readonly viaApp: Pick<IssueCommentViewerViaApp, 'name' | 'url'> | null
  readonly authorToRepoOwnerSponsorship: Pick<IssueCommentViewerSponsorship, 'createdAt' | 'isActive'> | null
  readonly lastUserContentEdit: {
    readonly editor: Pick<IssueCommentViewerEditor, 'login' | 'url'> | null
  } | null
  readonly pinnedBy: Pick<IssueCommentViewerPinnedBy, 'login'> | null
}

type Props = {
  anchorBaseUrl?: string
  comment: IssueCommentViewerCommentRowFragment
  reactable: IssueCommentViewerReactableFragment
  setIsEditing: () => void
  onReply: (quotedComment: string) => void
  onLinkClick?: (event: MouseEvent) => void
  refAttribute?: React.RefObject<HTMLDivElement | null>
  highlightedCommentId?: string
  navigate: (url: string) => void
  optimisticConnection?: OptimisticConnection
  commentSubjectAuthorLogin?: string
  createdViaEmail?: boolean
  hasDisabledActions?: boolean
  onEditHover?: () => void
  onSaveBody: (newBody: string, onCompleted: () => void, onError: () => void) => void
  issueUpdatesChannel?: string | null
  onSeeInTimeline?: (databaseId: number, returnFocus: HTMLButtonElement) => void
}

type InternalProps = Props & {
  commentAuthorLogin: string
  avatarUrl: string
  commentAuthorType?: CommentAuthorType
}

function isCommentAuthorType(type: string): type is CommentAuthorType {
  return (Object.values(CommentAuthorTypes) as readonly string[]).includes(type)
}

function convertCommentAuthorType(type: string | undefined): CommentAuthorType | undefined {
  return type && isCommentAuthorType(type) ? type : undefined
}

export function IssueCommentViewer({comment, ...rest}: Props) {
  const withAuthor = comment.author != null
  return withAuthor ? (
    <IssueCommentViewerInternal
      comment={comment}
      {...rest}
      commentAuthorLogin={comment.author.login}
      commentSubjectAuthorLogin={rest.commentSubjectAuthorLogin}
      avatarUrl={comment.author.avatarUrl}
      commentAuthorType={convertCommentAuthorType(comment.author.__typename)}
    />
  ) : (
    <Suspense fallback={<CommentLoading />}>
      <IssueCommentViewerInternalWithoutAuthor comment={comment} {...rest} />
    </Suspense>
  )
}

function IssueCommentViewerInternalWithoutAuthor(props: Props) {
  const {data: ghostUser} = useSuspenseQuery({
    queryKey: GHOST_USER_QUERY_KEY,
    queryFn: ({signal}) => fetchGhostUser(signal),
    staleTime: Infinity,
  })

  return (
    <IssueCommentViewerInternal
      {...props}
      commentAuthorLogin={ghostUser.login}
      avatarUrl={ghostUser.avatarUrl}
      commentAuthorType={CommentAuthorTypes.User}
      createdViaEmail={props.createdViaEmail}
    />
  )
}

function IssueCommentViewerInternal({
  anchorBaseUrl,
  comment: commentData,
  setIsEditing,
  onLinkClick,
  onReply,
  refAttribute,
  commentAuthorLogin,
  avatarUrl,
  highlightedCommentId,
  navigate,
  optimisticConnection,
  commentSubjectAuthorLogin,
  reactable,
  commentAuthorType,
  createdViaEmail = false,
  hasDisabledActions = false,
  onEditHover,
  onSaveBody,
  issueUpdatesChannel,
  onSeeInTimeline,
}: InternalProps) {
  const commentHidden = !!commentData.minimizedReason || commentData.isHidden
  const [isMinimized, setIsMinimized] = useSyncedState(commentHidden)
  const commentRef = useRef<HTMLDivElement>(null)

  const highlighted = String(commentData.databaseId) === highlightedCommentId
  const databaseId = commentData.databaseId

  const handleOnReply = (quotedText?: string) => {
    onReply(quotedText || getQuotedText(commentData.body))
  }

  const canReact = isLoggedIn() && !!commentData.issue && !commentData.issue.locked

  return (
    <div className={clsx(CLASS_NAMES.issueComment, styles.IssueCommentLayout)} ref={commentRef}>
      <div
        data-testid={TEST_IDS.commentViewerOuterBox(commentData.id)}
        ref={highlighted ? refAttribute : null}
        className={styles.IssueCommentContent}
      >
        <IssueCommentHeader
          anchorBaseUrl={anchorBaseUrl}
          comment={commentData}
          commentAuthorLogin={commentAuthorLogin}
          navigate={navigate}
          editComment={setIsEditing}
          onReplySelect={handleOnReply}
          isMinimized={isMinimized}
          onMinimize={setIsMinimized}
          avatarUrl={avatarUrl}
          commentRef={commentRef}
          optimisticConnection={optimisticConnection}
          commentSubjectAuthorLogin={commentSubjectAuthorLogin}
          commentAuthorType={commentAuthorType}
          hasDisabledActions={hasDisabledActions}
          onEditHover={onEditHover}
          onSeeInTimeline={
            databaseId == null || !onSeeInTimeline ? undefined : returnFocus => onSeeInTimeline(databaseId, returnFocus)
          }
        />
        {(!commentHidden || !isMinimized) && (
          <div className={styles.IssueCommentBody}>
            <CommentMarkdownViewer
              comment={commentData}
              onLinkClick={onLinkClick}
              createdViaEmail={createdViaEmail}
              viewerCanCreateIssues={commentData.repository.viewerCanCreateIssues}
              onSaveBody={onSaveBody}
              issueUpdatesChannel={issueUpdatesChannel}
            />
            <ReactionViewer
              subjectId={commentData.id}
              owner={commentData.repository.owner.login}
              repo={commentData.repository.name}
              issueUpdatesChannel={issueUpdatesChannel}
              liveUpdateTarget="comment"
              reactionGroups={reactable}
              canReact={canReact}
              showReactors={isFeatureEnabled('see_who_reacted')}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export type IssueCommentViewerMarkdownViewerFragment = Pick<
  IssueCommentViewerMarkdownViewerNode,
  'id' | 'body' | 'bodyHTML' | 'bodyVersion' | 'viewerCanUpdate'
>

type CommentMarkdownViewerProps = {
  comment: IssueCommentViewerMarkdownViewerFragment
  onLinkClick?: (event: MouseEvent) => void
  createdViaEmail: boolean
  viewerCanCreateIssues: boolean
  onSaveBody: (newBody: string, onCompleted: () => void, onError: () => void) => void
  issueUpdatesChannel?: string | null
}

type IssueCommentRefetchData = Pick<
  IssueCommentViewerMarkdownViewerFragment,
  'id' | 'body' | 'bodyHTML' | 'bodyVersion' | 'viewerCanUpdate'
>
type IssueCommentRefetchResult = {
  node?: IssueCommentRefetchData & {__typename: 'IssueComment'}
}

const LIVE_UPDATE_THROTTLE_MS = 250

type CommentUpdatedAliveNotification = {
  wait?: number
  comment_updated_id?: string
}

async function fetchIssueComment(id: string, signal?: AbortSignal): Promise<IssueCommentRefetchResult> {
  const params = new URLSearchParams({id, queryId: IssueCommentViewerRefetchServerQuery})
  const response = await reactFetchJSON(`/issues/issue-comment-viewer-refetch?${params}`, {signal})
  if (!response.ok) throw new Error(`Issue comment refetch failed (${response.status})`)

  const json = (await response.json()) as {
    data?: IssueCommentViewerRefetchServerQueryQuery & {
      node?: IssueCommentRefetchData & {__typename: 'IssueComment'}
    }
    errors?: unknown[]
  }
  if (!json.data) throw new Error('Issue comment refetch query returned no data')

  const node = json.data.node
  return {node: node?.__typename === 'IssueComment' ? node : undefined}
}

function CommentMarkdownViewer({
  comment: issueComment,
  onLinkClick,
  createdViaEmail,
  viewerCanCreateIssues,
  onSaveBody,
  issueUpdatesChannel,
}: CommentMarkdownViewerProps) {
  const id = issueComment.id
  const queryClient = useQueryClient()
  const queryKey = useMemo(() => VALUES.issueCommentViewerRefetchQueryKey(id), [id])
  const isCurrentCommentUpdated = useCallback(
    (data: CommentUpdatedAliveNotification) => data.comment_updated_id === id,
    [id],
  )
  useInvalidateOnAliveUpdate<CommentUpdatedAliveNotification>(issueUpdatesChannel, queryKey, {
    filter: isCurrentCommentUpdated,
    throttleTimeout: LIVE_UPDATE_THROTTLE_MS,
  })
  // eslint-disable-next-line @github-ui/github-monorepo/require-query-persister, @github-ui/github-monorepo/require-query-schema -- Seeded from the timeline; this existing SPQ has no runtime schema.
  const commentQuery = useQuery({
    queryKey,
    queryFn: ({signal}) => fetchIssueComment(id, signal),
    initialData: () => ({
      node: {
        __typename: 'IssueComment' as const,
        id,
        body: issueComment.body,
        bodyHTML: issueComment.bodyHTML,
        bodyVersion: issueComment.bodyVersion,
        viewerCanUpdate: issueComment.viewerCanUpdate,
      },
    }),
    staleTime: Infinity,
  })
  useEffect(() => {
    const cached = queryClient.getQueryData<IssueCommentRefetchResult>(queryKey)
    // Keep the cached view until the owning query resolves conflicting snapshots.
    if (
      cached?.node &&
      (cached.node.bodyVersion !== issueComment.bodyVersion || cached.node.bodyHTML !== issueComment.bodyHTML)
    ) {
      void queryClient.invalidateQueries({queryKey, exact: true})
    }
  }, [queryClient, queryKey, issueComment.bodyHTML, issueComment.bodyVersion])
  const optimisticData = useOptimisticData(commentQuery.data, {targets: [{queryKey}]})
  const commentData = {...issueComment, ...optimisticData.node}
  const {bodyHTML, viewerCanUpdate, body} = commentData

  /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side. */
  const html = (bodyHTML ?? '') as SafeHTMLString

  const issueCommentBodyRef = useRef<HTMLDivElement>(null)

  const fetchServerData = useCallback(
    (commentId: string) =>
      queryClient.fetchQuery({
        queryKey: VALUES.issueCommentViewerRefetchQueryKey(commentId),
        queryFn: ({signal}) => fetchIssueComment(commentId, signal),
        staleTime: 0,
      }),
    [queryClient],
  )
  const getFetchedHTML = useCallback((fetchResult: IssueCommentRefetchResult): string | undefined => {
    return fetchResult.node?.bodyHTML || undefined
  }, [])

  return (
    <RefreshVideoWrapper
      bodyHTML={html}
      bodyRef={issueCommentBodyRef}
      id={id}
      fetchData={fetchServerData}
      getFetchedHTML={getFetchedHTML}
    >
      <IssueMarkdownViewer
        html={html}
        markdown={body}
        viewerCanCreateIssues={viewerCanCreateIssues}
        viewerCanUpdate={viewerCanUpdate}
        onSave={onSaveBody}
        onLinkClick={createdViaEmail ? undefined : onLinkClick}
        createdViaEmail={createdViaEmail}
        issueCommentBodyRef={issueCommentBodyRef}
      />
    </RefreshVideoWrapper>
  )
}
